import { Button, Drawer, DrawerProps, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useInfiniteScroll } from 'ahooks';
import { getTaskLogList } from 'services/api/log';
import Empty from 'components/Empty';
import { useTranslate } from '@tolgee/react';
import { useEffect, useRef, useState } from 'react';
import { css, cx } from '@emotion/css';

import { LogItem } from './LogItem';
import { logList } from 'views/Aide/service';

export interface LogDrawerProps extends DrawerProps {
  taskId?: string;
  scenarioType: Marketing.AllScenarioType;
  round?: number;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  onClose: () => void;
}

export const LogDrawer = (props: LogDrawerProps) => {
  const {
    taskId,
    visible,
    platform,
    region,
    scenarioType,
    round = 1,
    onClose,
    ...drawerProps
  } = props;
  const [inputText, setInputText] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);
  const [empty, setEmpty] = useState(false);
  const { t } = useTranslate();

  const {
    data: logData,
    loadingMore,
    noMore,
  } = useInfiniteScroll(
    (d) =>
      visible
        ? (taskId
            ? getTaskLogList({
                taskId,
                offset: d?.offset ?? 0,
                pageSize: 50,
              })
            : logList({
                scenarioType,
                platform,
                region,
                round,
                offset: d?.offset ?? 0,
                pageSize: 50,
              })
          ).then((res) => ({
            list: res.logs ?? [],
            offset: res.nextOffset,
            hasNext: res.hasNext,
          }))
        : Promise.resolve({
            list: [],
            offset: 0,
            hasNext: false,
          }),
    {
      target: contentRef,
      isNoMore: (d) => {
        return !d?.hasNext;
      },
      reloadDeps: [visible, taskId],
    },
  );

  useEffect(() => {
    setEmpty(false);
    setTimeout(() => {
      if (inputText && contentRef?.current) {
        const hiddenLen = contentRef.current.querySelectorAll('div[data-hidden="true"]').length;
        if (hiddenLen === logData?.list?.length) {
          setEmpty(true);
        } else {
          setEmpty(false);
        }
      }
    });
  }, [inputText, logData?.list?.length]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Drawer
      {...drawerProps}
      className={drawer}
      visible={visible}
      footer={<Button onClick={handleClose}>{t('marketing.关闭')}</Button>}
      onClose={handleClose}
    >
      {logData?.list?.length ? (
        <>
          <div className="px-4 pt-4">
            <Input
              value={inputText}
              placeholder={t('search.searchIptTip')}
              className="!w-72 mb-2"
              suffix={<SearchOutlined />}
              allowClear
              onChange={(e) => {
                setInputText(e.target.value);
              }}
            />
          </div>
          <div ref={contentRef} className={cx('flex-1', 'overflow-y-auto', empty ? 'hidden' : '')}>
            <div className="p-4">
              {logData?.list?.map((log, index) => (
                <LogItem
                  key={index}
                  data={log}
                  platform={platform}
                  region={region}
                  round={round}
                  searchWord={inputText}
                />
              ))}
              {!empty && (
                <div className="flex justify-center text-content-200">
                  {!noMore && loadingMore && t('trade.加载中')}
                  {noMore && t('loadMore.noMore')}
                </div>
              )}
            </div>
          </div>
          {empty && (
            <Empty
              className="flex flex-col items-center"
              description={
                <div>
                  <div>{t('voucher.searchEmpty')}</div>
                  <div>{t('TaskOperationLog.searchDesc')}</div>
                </div>
              }
            />
          )}
        </>
      ) : (
        <Empty className="!mt-12" description={t('storeSelector.暂无数据')} />
      )}
    </Drawer>
  );
};

const drawer = css`
  .ant-drawer-body {
    padding: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
`;
